/**
 * <upsell-offers>
 *
 * Intercepts the add-to-cart click and adds the suitcase plus every ticked
 * add-on as separate cart lines in one /cart/add.js call.
 *
 * Offers are independent checkboxes. Each one carries its own discount, which
 * applies to that offer's add-on lines only — the suitcase is never discounted.
 * Ticking two offers is simply two independent discounts.
 *
 * Line item properties:
 *   _bundle   on every line of a group, unique per add-to-cart, so two
 *             identical groups stay separate
 *   _offer    on add-on lines only: the offer's slot (its slot_s field, or
 *             its handle), which is what the Function's config is keyed by
 *   _parent   on add-on lines only: the suitcase variant id
 *
 * The suitcase line deliberately carries no _offer — it is never discounted,
 * and _bundle is all the Function needs to confirm the parent is present.
 *
 * An offer the snippet marked `data-no-discount` is one whose add-ons are
 * already marked down harder than the offer percentage. The page quotes that
 * markdown, so those lines carry no _offer either: the Function must not cut
 * the sale price a second time. They keep _bundle and _parent, so the drawer
 * still groups them under the suitcase.
 *
 * The discount Function reads _offer against its own config metafield — the
 * percentage is never taken from a line property, since the AJAX cart API is
 * writable by the customer. It also requires a line with the same _bundle and
 * no _parent to exist, so removing the suitcase drops the add-on discounts.
 *
 * Ticking nothing falls through to the theme's normal add-to-cart.
 *
 * Theme seam (Prestige 5.6.2):
 *   - ProductVariants binds add-to-cart to a DELEGATED CLICK on the section
 *     element and preventDefaults it, so the form never fires a submit event.
 *     We listen on the form in the capture phase, which runs before the
 *     delegate root sees the click.
 *   - CartSection listens for `product:added` on document: it adds
 *     detail.quantity to its own item counter, re-renders the mini-cart
 *     section and then opens the drawer. There is no drawer instance exposed
 *     to call open() on directly.
 */

(function () {
  var ADD_TO_CART = '[data-action="add-to-cart"]';

  class UpsellOffers extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form[action*="/cart/add"]');
      if (!this.form) {
        console.warn('[upsell-offers] not inside a product form — skipping');
        return;
      }

      this.parentVariantId = this.dataset.parentVariant;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);

      this.form.addEventListener('click', this.onClick, true);
      this.addEventListener('change', this.onChange);

      this.syncPaymentButton();
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('click', this.onClick, true);
      this.removeEventListener('change', this.onChange);
    }

    /** Every ticked offer, in the order the snippet rendered them. */
    get selection() {
      var checked = this.querySelectorAll('input[type="checkbox"]:checked');

      return [].map
        .call(checked, function (input) {
          var variants = (input.dataset.variants || '')
            .split(',')
            .map(function (id) {
              return id.trim();
            })
            .filter(Boolean);

          return {
            offer: input.value,
            variants: variants,
            discount: !input.hasAttribute('data-no-discount'),
          };
        })
        .filter(function (entry) {
          return entry.offer && entry.variants.length;
        });
    }

    onChange() {
      this.syncPaymentButton();
      this.clearError();
    }

    onClick(event) {
      var button = event.target.closest(ADD_TO_CART);
      if (!button || !this.form.contains(button)) return;

      var selection = this.selection;
      if (!selection.length) return; // nothing ticked — let the theme handle it

      // Stop both the native submit and the theme's delegated click handler.
      event.preventDefault();
      event.stopPropagation();

      this.addBundle(selection);
    }

    async addBundle(selection) {
      this.setBusy(true);
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      var bundleId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      var parentVariantId = this.parentVariantId;

      var items = [
        {
          id: Number(parentVariantId),
          quantity: 1,
          properties: { _bundle: bundleId },
        },
      ];

      selection.forEach(function (entry) {
        entry.variants.forEach(function (id) {
          var properties = {
            _bundle: bundleId,
            _parent: parentVariantId,
          };

          // Withheld for a marked-down offer — see the header.
          if (entry.discount) properties._offer = entry.offer;

          items.push({ id: Number(id), quantity: 1, properties: properties });
        });
      });

      try {
        var cartAddUrl = (window.routes && window.routes.cartAddUrl) || '/cart/add';

        var response = await fetch(cartAddUrl + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: items }),
        });

        var data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'Hinzufügen fehlgeschlagen');
        }

        this.dispatchEvent(
          new CustomEvent('upsell:added', {
            bubbles: true,
            detail: {
              bundleId: bundleId,
              offers: selection.map(function (entry) {
                return entry.offer;
              }),
              items: data.items || [],
            },
          })
        );

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'add_to_cart' });

        this.openCart(items.length);
      } catch (error) {
        console.error('[upsell-offers]', error);
        this.showError(error.message);
      } finally {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        this.setBusy(false);
      }
    }

    /**
     * Re-renders the mini-cart and opens the drawer. CartSection tracks its own
     * item counter, so detail.quantity must be the number of lines we added.
     */
    openCart(lineCount) {
      document.dispatchEvent(
        new CustomEvent('product:added', {
          bubbles: true,
          detail: {
            variant: { id: Number(this.parentVariantId) },
            quantity: lineCount,
          },
        })
      );
    }

    /**
     * The dynamic checkout button posts the form straight to checkout and would
     * skip the add-ons, so it is hidden while anything is ticked.
     */
    syncPaymentButton() {
      var paymentButton = this.form.querySelector('.shopify-payment-button');
      if (!paymentButton) return;
      paymentButton.hidden = this.selection.length > 0;
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', busy ? 'true' : 'false');

      document.querySelectorAll('.ProductForm__AddToCart').forEach(function (button) {
        button.toggleAttribute('disabled', busy);
      });
    }

    showError(message) {
      var node = this.querySelector('.upsell-offers__error');
      if (!node) {
        node = document.createElement('p');
        node.className = 'upsell-offers__error';
        node.setAttribute('role', 'alert');
        this.appendChild(node);
      }
      node.textContent = message;
    }

    clearError() {
      var node = this.querySelector('.upsell-offers__error');
      if (node) node.remove();
    }
  }

  if (!customElements.get('upsell-offers')) {
    customElements.define('upsell-offers', UpsellOffers);
  }
})();
